'use client';

import { useState, useEffect, useRef } from 'react';
import { Copy, Check, Contrast, RefreshCw, Palette } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { colorPickerContent } from '../../data/color-picker-content';
import Link from 'next/link';

type ColorFormat = 'hex' | 'rgb' | 'hsl';

interface ColorRGB {
  r: number;
  g: number;
  b: number;
  a?: number;
}

interface ColorHSL {
  h: number;
  s: number;
  l: number;
  a?: number;
}

export default function ColorPickerTool() {
  // Color states
  const [color, setColor] = useState<string>('#3b82f6');
  const [format, setFormat] = useState<ColorFormat>('hex');
  const [copied, setCopied] = useState(false);
  const [showPalette, setShowPalette] = useState(false);
  const [contrastRatio, setContrastRatio] = useState(0);
  const [textColor, setTextColor] = useState('#000000');
  const [palette, setPalette] = useState<string[]>([]);
  const colorInputRef = useRef<HTMLInputElement>(null);

  // Convert color between formats
  const convertColor = (value: string, targetFormat: ColorFormat): string => {
    if (!value) return '';

    try {
      // Parse input color
      let r = 0, g = 0, b = 0, a = 1;

      // Handle HEX
      if (value.startsWith('#')) {
        const hex = value.replace('#', '');
        if (hex.length === 3) {
          r = parseInt(hex[0] + hex[0], 16);
          g = parseInt(hex[1] + hex[1], 16);
          b = parseInt(hex[2] + hex[2], 16);
        } else if (hex.length === 6 || hex.length === 8) {
          r = parseInt(hex.substring(0, 2), 16);
          g = parseInt(hex.substring(2, 4), 16);
          b = parseInt(hex.substring(4, 6), 16);
          if (hex.length === 8) {
            a = parseInt(hex.substring(6, 8), 16) / 255;
          }
        }
      }
      // Handle RGB/RGBA
      else if (value.startsWith('rgb')) {
        const parts = value.match(/\d+(\.\d+)?/g)?.map(Number) || [];
        if (parts.length >= 3) {
          [r, g, b] = parts;
          a = parts[3] ?? 1;
        }
      }
      // Handle HSL/HSLA
      else if (value.startsWith('hsl')) {
        const parts = value.match(/(\d+(\.\d+)?)/g)?.map(Number) || [];
        if (parts.length >= 3) {
          const [h, s, l] = parts;
          a = parts[3] ?? 1;
          return hslToFormat(h, s, l, a, targetFormat);
        }
      }

      // Convert to target format
      switch (targetFormat) {
        case 'hex':
          return rgbToHex(r, g, b, a);
        case 'rgb':
          return a === 1
            ? `rgb(${r}, ${g}, ${b})`
            : `rgba(${r}, ${g}, ${b}, ${a})`;
        case 'hsl':
          return rgbToHsl(r, g, b, a);
        default:
          return value;
      }
    } catch (e) {
      console.error('Error converting color:', e);
      return value;
    }
  };

  // RGB to HEX
  const rgbToHex = (r: number, g: number, b: number, a: number = 1): string => {
    const toHex = (c: number) => {
      const hex = Math.round(c).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    const alpha = a < 1 ? toHex(a * 255) : '';
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${alpha}`.toUpperCase();
  };

  // RGB to HSL
  const rgbToHsl = (r: number, g: number, b: number, a: number = 1): string => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }

      h = Math.round(h * 60);
      if (h < 0) h += 360;
    }

    s = Math.round(s * 100);
    l = Math.round(l * 100);

    return a === 1
      ? `hsl(${h}, ${s}%, ${l}%)`
      : `hsla(${h}, ${s}%, ${l}%, ${a})`;
  };

  // HSL to target format
  const hslToFormat = (h: number, s: number, l: number, a: number, targetFormat: ColorFormat): string => {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0, g = 0, b = 0;

    if (h >= 0 && h < 60) {
      [r, g] = [c, x, 0];
    } else if (h < 120) {
      [r, g] = [x, c, 0];
    } else if (h < 180) {
      [r, g, b] = [0, c, x];
    } else if (h < 240) {
      [r, g, b] = [0, x, c];
    } else if (h < 300) {
      [r, g, b] = [x, 0, c];
    } else {
      [r, g, b] = [c, 0, x];
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    switch (targetFormat) {
      case 'hex':
        return rgbToHex(r, g, b, a);
      case 'rgb':
        return a === 1
          ? `rgb(${r}, ${g}, ${b})`
          : `rgba(${r}, ${g}, ${b}, ${a})`;
      case 'hsl':
      default:
        return a === 1
          ? `hsl(${h}, ${Math.round(s * 100)}%, ${Math.round((l + m) * 100)}%)`
          : `hsla(${h}, ${Math.round(s * 100)}%, ${Math.round((l + m) * 100)}%, ${a})`;
    }
  };

  // Calculate contrast ratio
  const calculateContrastRatio = (bgColor: string, textColor: string): number => {
    const getLuminance = (color: string): number => {
      const hex = color.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;

      const [red, green, blue] = [r, g, b].map(c => {
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });

      return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
    };

    const lum1 = getLuminance(bgColor);
    const lum2 = getLuminance(textColor);
    const lighter = Math.max(lum1, lum2);
    const darker = Math.min(lum1, lum2);

    return (lighter + 0.05) / (darker + 0.05);
  };

  // Generate color palette
  const generatePalette = (baseColor: string) => {
    try {
      const hex = baseColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      const colors: string[] = [];

      // Generate tints and shades
      for (let i = 0; i <= 10; i++) {
        const t = i / 10;
        const newR = Math.round(r + (255 - r) * t);
        const newG = Math.round(g + (255 - g) * t);
        const newB = Math.round(b + (255 - b) * t);
        colors.push(rgbToHex(newR, newG, newB));
      }

      // Generate complementary colors
      const compR = 255 - r;
      const compG = 255 - g;
      const compB = 255 - b;
      colors.push(rgbToHex(compR, compG, compB));

      setPalette(colors);
    } catch (e) {
      console.error('Error generating palette:', e);
    }
  };

  // Handle color change
  const handleColorChange = (newColor: string) => {
    setColor(newColor);
    generatePalette(newColor);
  };

  // Copy color to clipboard
  const copyToClipboard = (text: string) => {
    if (!text) return;

    // Fallback for browsers that don't support the Clipboard API
    if (!navigator.clipboard) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        toast.success('Copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
        toast.error('Failed to copy to clipboard');
      }
      document.body.removeChild(textArea);
      return;
    }

    // Modern Clipboard API
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }).catch((err) => {
      console.error('Failed to copy text: ', err);
      toast.error('Failed to copy to clipboard');
    });
  };

  // Format color for display
  const formatColor = (color: string, targetFormat: ColorFormat) => {
    if (!color) return '';
    return convertColor(color, targetFormat);
  };

  // Get text color based on background
  const getTextColor = (bgColor: string) => {
    try {
      const hex = bgColor.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // Calculate luminance (perceived brightness)
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

      // Return black for light colors, white for dark colors
      return luminance > 0.5 ? '#000000' : '#ffffff';
    } catch (e) {
      return '#000000';
    }
  };

  // Get contrast rating
  const getContrastRating = (ratio: number) => {
    if (ratio >= 7) return { text: 'AAA', color: 'text-green-600' };
    if (ratio >= 4.5) return { text: 'AA', color: 'text-blue-600' };
    if (ratio >= 3) return { text: 'AA Large', color: 'text-yellow-600' };
    return { text: 'Fail', color: 'text-red-600' };
  };

  // Initialize
  useEffect(() => {
    generatePalette(color);
    setTextColor(getTextColor(color));
  }, []);

  // Update contrast ratio when colors change
  useEffect(() => {
    try {
      const bgHex = color.startsWith('#') ? color : `#${color}`;
      const textHex = textColor.startsWith('#') ? textColor : `#${textColor}`;
      const ratio = calculateContrastRatio(bgHex, textHex);
      setContrastRatio(Number(ratio.toFixed(2)));
    } catch (e) {
      console.error('Error calculating contrast ratio:', e);
    }
  }, [color, textColor]);

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="mb-8">
        <ToolPageIntro title="Color Picker">
                Pick colors from images or choose from color palettes with our free online color picker. Get HEX, RGB, and HSL values for web design and development.
            </ToolPageIntro>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Color Picker */}
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Color Picker</h2>

            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <div className="relative">
                <div
                  className="w-32 h-32 rounded-lg border-2 border-gray-200 shadow-sm"
                  style={{ backgroundColor: color }}
                />
                <button
                  onClick={() => colorInputRef.current?.click()}
                  className="absolute -bottom-2 -right-2 bg-white p-1.5 rounded-full shadow-md hover:bg-gray-50 transition-colors"
                  title="Pick a color"
                >
                  <Palette className="h-5 w-5 text-gray-700" />
                </button>
                <input
                  ref={colorInputRef}
                  type="color"
                  value={color}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="absolute opacity-0 w-0 h-0"
                />
              </div>

              <div className="flex-1 space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color Value
                  </label>
                  <div className="flex rounded-md shadow-sm">
                    <input
                      type="text"
                      value={color}
                      onChange={(e) => handleColorChange(e.target.value)}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md border border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="#RRGGBB"
                    />
                    <button
                      onClick={() => copyToClipboard(color)}
                      className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100"
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Format
                  </label>
                  <div className="flex rounded-md shadow-sm">
                    {(['hex', 'rgb', 'hsl'] as const).map((fmt) => (
                      <button
                        key={fmt}
                        type="button"
                        onClick={() => setFormat(fmt)}
                        className={`flex-1 py-2 text-sm font-medium ${format === fmt
                          ? 'bg-blue-100 text-blue-700 border-blue-500'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
                          } border ${fmt === 'hex' ? 'rounded-l-md' : ''} ${fmt === 'hsl' ? 'rounded-r-md' : ''
                          }`}
                      >
                        {fmt.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="pt-2">
                  <div className="text-sm font-medium text-gray-700 mb-1">
                    {format.toUpperCase()} Value
                  </div>
                  <div className="flex rounded-md shadow-sm">
                    <input
                      type="text"
                      value={formatColor(color, format)}
                      readOnly
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md border border-gray-300 bg-gray-50 text-gray-700 sm:text-sm"
                    />
                    <button
                      onClick={() => copyToClipboard(formatColor(color, format))}
                      className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-100 text-gray-500 hover:bg-gray-200"
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contrast Checker */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Contrast className="h-5 w-5 mr-2 text-blue-600" />
              Contrast Checker
            </h2>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Background</div>
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-8 h-8 rounded border border-gray-200"
                      style={{ backgroundColor: color }}
                    />
                    <div className="text-sm text-gray-600 truncate">
                      {color}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-sm font-medium text-gray-700 mb-1">Text Color</div>
                  <div className="flex items-center space-x-2">
                    <div
                      className="w-8 h-8 rounded border border-gray-200 flex items-center justify-center"
                      style={{ backgroundColor: textColor }}
                    >
                      <span
                        className="text-xs font-bold"
                        style={{ color: getTextColor(textColor) }}
                      >
                        Aa
                      </span>
                    </div>
                    <div className="flex-1">
                      <select
                        value={textColor}
                        onChange={(e) => setTextColor(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      >
                        <option value="#000000">Black (#000000)</option>
                        <option value="#ffffff">White (#FFFFFF)</option>
                        <option value={getTextColor(color)}>
                          {getTextColor(color) === '#000000' ? 'Black' : 'White'} (Auto)
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-2">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">Contrast Ratio</span>
                  <span className="text-sm font-medium">
                    {contrastRatio}:1
                  </span>
                </div>

                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full"
                    style={{
                      width: `${Math.min(100, (contrastRatio / 10) * 100)}%`,
                      backgroundColor: contrastRatio >= 4.5 ? '#10B981' : contrastRatio >= 3 ? '#F59E0B' : '#EF4444'
                    }}
                  />
                </div>

                <div className="mt-2 flex justify-between items-center">
                  <span className="text-xs text-gray-500">0</span>
                  <span className="text-xs text-gray-500">10+</span>
                </div>

                <div className="mt-3">
                  <div className="text-sm text-gray-700 mb-1">WCAG Compliance:</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { level: 'AAA', minRatio: 7 },
                      { level: 'AA', minRatio: 4.5 },
                      { level: 'AA Large', minRatio: 3, desc: '(18pt+ or 14pt+bold)' },
                    ].map(({ level, minRatio, desc }) => {
                      const passes = contrastRatio >= minRatio;
                      return (
                        <div
                          key={level}
                          className={`flex items-center p-2 rounded-md ${passes ? 'bg-green-50' : 'bg-gray-50'
                            }`}
                        >
                          <div
                            className={`w-5 h-5 rounded-full flex items-center justify-center mr-2 ${passes ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                              }`}
                          >
                            {passes ? '✓' : '—'}
                          </div>
                          <div>
                            <div className="text-sm font-medium">{level}</div>
                            <div className="text-xs text-gray-500">
                              {minRatio}:1{desc ? ` ${desc}` : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Color Palette */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-medium text-gray-900">Color Palette</h2>
            <button
              onClick={() => setShowPalette(!showPalette)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showPalette ? 'Hide' : 'Show'} Palette
            </button>
          </div>

          {showPalette ? (
            <div className="space-y-4">
              <div className="grid grid-cols-5 gap-2">
                {palette.map((color, index) => (
                  <div key={index} className="relative group">
                    <button
                      onClick={() => {
                        handleColorChange(color);
                        toast.success('Color selected!');
                      }}
                      className="w-full h-12 rounded-md border border-gray-200 shadow-sm hover:ring-2 hover:ring-blue-500 transition-all"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                    <div className="absolute -bottom-6 left-0 right-0 text-center">
                      <span className="text-xs text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">
                        {color}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Preview</h3>
                <div
                  className="p-6 rounded-lg border border-gray-200 flex items-center justify-center"
                  style={{ backgroundColor: color }}
                >
                  <p
                    className="text-lg font-medium text-center"
                    style={{ color: textColor }}
                  >
                    The quick brown fox jumps over the lazy dog.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Palette className="mx-auto h-10 w-10 text-gray-300" />
              <p className="mt-2 text-sm text-gray-500">
                Click "Show Palette" to generate a color palette
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
        <h3 className="font-medium text-blue-800 mb-2">How to use the Color Picker</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-blue-700 mb-1">Color Selection:</h4>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
              <li>Click the palette icon to open the color picker</li>
              <li>Or enter a color value manually (HEX, RGB, or HSL)</li>
              <li>Switch between color formats using the format buttons</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-blue-700 mb-1">Contrast Checker:</h4>
            <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
              <li>Check the contrast between your color and text</li>
              <li>Get WCAG compliance ratings</li>
              <li>View the contrast ratio and visual preview</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Informational Content */}
      <div className="mt-12">
        <InformationalContent
          title={colorPickerContent.title}
          description={colorPickerContent.description}
          content={colorPickerContent.content}
          faqs={colorPickerContent.faqs}
          relatedTools={colorPickerContent.relatedTools}
        />
      </div>
    </div>
  );
}
